import {useRef} from 'react';
import type {ChatOnLoadParams, ChatMessageTextPart, ChatMessageDesignPart} from '@Components/ai-chat-window/ai-chat-window.types';
import {ChatMessagePartType} from '@Components/ai-chat-window/ai-chat-window.types';
import type {DesignAttachment} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {PromptAttachmentType} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';

export interface ChatOnLoadFill {
  initialPrompt?: string;
  initialAttachments?: DesignAttachment[];
  initialPinnedAspectRatio: string | null;
  initialPinnedBrandKitId: string | null;
}

const computeChatOnLoadFill = (chatOnLoadParams?: ChatOnLoadParams): ChatOnLoadFill => {
  const initialPinnedAspectRatio = chatOnLoadParams?.preferences?.aspectRatio ?? null;
  const initialPinnedBrandKitId = chatOnLoadParams?.preferences?.brandKitId ?? null;

  if (!chatOnLoadParams?.userRequestParts?.length || chatOnLoadParams.sendOnLoad) {
    return {initialPinnedAspectRatio, initialPinnedBrandKitId};
  }

  const parts = chatOnLoadParams.userRequestParts;
  const textPart = parts.find((p): p is ChatMessageTextPart => p.type === ChatMessagePartType.TEXT);
  const designParts = parts.filter((p): p is ChatMessageDesignPart => p.type === ChatMessagePartType.DESIGN);

  const prompt = textPart?.text ?? undefined;
  const attachments = designParts
    .filter((p): p is ChatMessageDesignPart & {previewUrl: string} => p.previewUrl !== undefined)
    .map((p): DesignAttachment => ({
      type: PromptAttachmentType.DESIGN,
      designId: p.hashedId,
      previewURL: p.previewUrl,
      pageHashedId: p.pageHashedId,
    }));

  return {
    initialPrompt: prompt,
    initialAttachments: attachments.length > 0 ? attachments : undefined,
    initialPinnedAspectRatio,
    initialPinnedBrandKitId,
  };
};

/**
 * Extracts prompt box fill data from chatOnLoadParams.
 * Prompt and attachments only apply when `sendOnLoad` is absent (fill-only mode, not auto-send).
 * The pinned aspect ratio and brand kit apply in both modes: sending consumes the prompt but not the pins.
 * Computed once at mount from stable page-load props.
 */
export const useChatOnLoadFill = (chatOnLoadParams?: ChatOnLoadParams): ChatOnLoadFill => {
  const fillRef = useRef<ChatOnLoadFill | null>(null);
  fillRef.current ??= computeChatOnLoadFill(chatOnLoadParams);
  return fillRef.current;
};
